import React, { Fragment, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import {
    AbaId,
    BUSCAR_ATLETAS,
    BUSCAR_TORNEIOS,
    BUSCAR_LIGAS,
    BUSCAR_SEGUINDO,
    BuscarAtletasData,
    BuscarAtletasVars,
    BuscarTorneiosData,
    BuscarTorneiosVars,
    BuscarLigasData,
    BuscarLigasVars,
    BuscarSeguindoData,
    BuscarSeguindoVars,
    PesquisaAtletaItem,
    PesquisaTorneioItem,
    PesquisaLigaItem,
    PesquisaConnection,
} from '../services/pesquisaService';
import Header from '../components/Header';
import AtletaCard from '../components/AtletaCard';
import TorneioCardPesquisa from '../components/TorneioCardPesquisa';
import LigaCardPesquisa from '../components/LigaCardPesquisa';
import Pagination from '../components/Pagination';
import { useUserRole } from '../hooks/useUserRole';

const PAGE_SIZE = 12;

const SearchPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const q = searchParams.get('q') ?? '';

    // Aba "Seguindo" só faz sentido para atletas. O backend exige hasRole('ATLETA') no
    // resolver searchSeguindo via @PreAuthorize, então um admin que conseguisse disparar
    // a query mesmo assim receberia FORBIDDEN. Aqui escondemos a aba para evitar a chamada.
    const { isAdmin } = useUserRole();
    const abaSeguindoVisivel = !isAdmin;

    const [abaAtiva, setAbaAtiva] = useState<AbaId>('atletas');

    // Se o role do usuário mudar para admin durante a sessão (refresh de token, etc.) e a
    // aba 'seguindo' estava ativa, voltamos para 'atletas' — caso contrário a aba ficaria
    // "ativa" sem botão visível e o useEffect ainda dispararia fetchSeguindo (FORBIDDEN).
    useEffect(() => {
        if (!abaSeguindoVisivel && abaAtiva === 'seguindo') {
            setAbaAtiva('atletas');
        }
    }, [abaSeguindoVisivel, abaAtiva]);

    const [paginaAtletas, setPaginaAtletas] = useState(0);
    const [paginaTorneios, setPaginaTorneios] = useState(0);
    const [paginaLigas, setPaginaLigas] = useState(0);
    const [paginaSeguindo, setPaginaSeguindo] = useState(0);

    const [fetchAtletas, atletasResult] =
        useLazyQuery<BuscarAtletasData, BuscarAtletasVars>(
            BUSCAR_ATLETAS,
            { fetchPolicy: 'no-cache' }
        );

    const [fetchTorneios, torneiosResult] =
        useLazyQuery<BuscarTorneiosData, BuscarTorneiosVars>(
            BUSCAR_TORNEIOS,
            { fetchPolicy: 'no-cache' }
        );

    const [fetchLigas, ligasResult] =
        useLazyQuery<BuscarLigasData, BuscarLigasVars>(
            BUSCAR_LIGAS,
            { fetchPolicy: 'no-cache' }
        );

    const [fetchSeguindo, seguindoResult] =
        useLazyQuery<BuscarSeguindoData, BuscarSeguindoVars>(
            BUSCAR_SEGUINDO,
            { fetchPolicy: 'no-cache' }
        );

    // Dispara a query da aba ativa quando q, aba ou página mudam.
    // Cada aba mantém sua própria página para que voltar à aba não recomece do zero.
    useEffect(() => {
        if (!q || q.trim().length < 3) return;
        const vars = { termo: q.trim(), size: PAGE_SIZE };
        if (abaAtiva === 'atletas') {
            fetchAtletas({ variables: { ...vars, page: paginaAtletas } });
        } else if (abaAtiva === 'torneios') {
            fetchTorneios({ variables: { ...vars, page: paginaTorneios } });
        } else if (abaAtiva === 'ligas') {
            fetchLigas({ variables: { ...vars, page: paginaLigas } });
        } else if (abaAtiva === 'seguindo') {
            fetchSeguindo({ variables: { ...vars, page: paginaSeguindo } });
        }
    }, [abaAtiva, q, paginaAtletas, paginaTorneios, paginaLigas, paginaSeguindo,
        fetchAtletas, fetchTorneios, fetchLigas, fetchSeguindo]);

    // Resetar para página 0 ao trocar de aba; sem isso a aba destino abriria
    // numa página intermediária herdada da aba anterior.
    const mudarAba = (aba: AbaId) => {
        setAbaAtiva(aba);
        if (aba === 'atletas') setPaginaAtletas(0);
        else if (aba === 'torneios') setPaginaTorneios(0);
        else if (aba === 'ligas') setPaginaLigas(0);
        else if (aba === 'seguindo') setPaginaSeguindo(0);
    };

    interface AbaState<T> {
        connection: PesquisaConnection<T> | null;
        loading: boolean;
        error: boolean;
    }

    const atletasState: AbaState<PesquisaAtletaItem> = {
        connection: atletasResult.data?.searchAtletas ?? null,
        loading: atletasResult.loading,
        error: !!atletasResult.error,
    };
    const torneiosState: AbaState<PesquisaTorneioItem> = {
        connection: torneiosResult.data?.searchTorneios ?? null,
        loading: torneiosResult.loading,
        error: !!torneiosResult.error,
    };
    const ligasState: AbaState<PesquisaLigaItem> = {
        connection: ligasResult.data?.searchLigas ?? null,
        loading: ligasResult.loading,
        error: !!ligasResult.error,
    };
    const seguindoState: AbaState<PesquisaAtletaItem> = {
        connection: seguindoResult.data?.searchSeguindo ?? null,
        loading: seguindoResult.loading,
        error: !!seguindoResult.error,
    };

    function renderEstadoNaoPopulado<T>(state: AbaState<T>): React.ReactNode {
        if (state.loading) {
            return (
                <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                    </div>
                </div>
            );
        }
        if (state.error) {
            return (
                <p className="text-center text-danger py-4">
                    Ocorreu um erro ao buscar resultados. Tente novamente.
                </p>
            );
        }
        if (state.connection && state.connection.totalItems === 0) {
            return (
                <p className="text-center text-grey-500 py-4">
                    Nenhum resultado encontrado para '{q}'
                </p>
            );
        }
        return null;
    }

    const termoValido = q.trim().length >= 3;

    return (
        <Fragment>
            <Header />

            <div className="main-content right-chat-active">
                <div className="middle-sidebar-bottom">
                    <div className="middle-sidebar-left">
                        <div className="row feed-body">
                            <div className="col-xl-12">
                                {!termoValido ? (
                                    <p className="text-center text-grey-500 py-4">
                                        Digite pelo menos 3 caracteres no campo de busca para ver os resultados.
                                    </p>
                                ) : (
                                <>
                                <h2 className="fw-700 mb-3">Resultados para "{q}"</h2>

                                <ul className="nav nav-tabs border-0 mb-3" role="tablist">
                                    <li className="nav-item" role="presentation">
                                        <button
                                            className={`nav-link ${abaAtiva === 'atletas' ? 'active' : ''}`}
                                            id="atletas-tab"
                                            type="button"
                                            role="tab"
                                            aria-controls="atletas"
                                            aria-selected={abaAtiva === 'atletas'}
                                            onClick={() => mudarAba('atletas')}
                                        >
                                            Atletas
                                        </button>
                                    </li>
                                    <li className="nav-item" role="presentation">
                                        <button
                                            className={`nav-link ${abaAtiva === 'torneios' ? 'active' : ''}`}
                                            id="torneios-tab"
                                            type="button"
                                            role="tab"
                                            aria-controls="torneios"
                                            aria-selected={abaAtiva === 'torneios'}
                                            onClick={() => mudarAba('torneios')}
                                        >
                                            Torneios
                                        </button>
                                    </li>
                                    <li className="nav-item" role="presentation">
                                        <button
                                            className={`nav-link ${abaAtiva === 'ligas' ? 'active' : ''}`}
                                            id="ligas-tab"
                                            type="button"
                                            role="tab"
                                            aria-controls="ligas"
                                            aria-selected={abaAtiva === 'ligas'}
                                            onClick={() => mudarAba('ligas')}
                                        >
                                            Ligas
                                        </button>
                                    </li>
                                    {abaSeguindoVisivel && (
                                        <li className="nav-item" role="presentation">
                                            <button
                                                className={`nav-link ${abaAtiva === 'seguindo' ? 'active' : ''}`}
                                                id="seguindo-tab"
                                                type="button"
                                                role="tab"
                                                aria-controls="seguindo"
                                                aria-selected={abaAtiva === 'seguindo'}
                                                onClick={() => mudarAba('seguindo')}
                                            >
                                                Seguindo
                                            </button>
                                        </li>
                                    )}
                                </ul>

                                <div className="tab-content">
                                    <div
                                        id="atletas"
                                        className={`tab-pane fade ${abaAtiva === 'atletas' ? 'show active' : ''}`}
                                        role="tabpanel"
                                        aria-labelledby="atletas-tab"
                                    >
                                        {renderEstadoNaoPopulado(atletasState)}
                                        {atletasState.connection && atletasState.connection.totalItems > 0 && (
                                            <>
                                                <div className="row gy-3">
                                                    {atletasState.connection.items.map((atleta) => (
                                                        <div key={atleta.id} className="col-md-6 col-lg-4">
                                                            <AtletaCard atleta={atleta} />
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="mt-3">
                                                    <Pagination
                                                        currentPage={paginaAtletas}
                                                        totalPages={atletasState.connection.totalPages}
                                                        onPageChange={setPaginaAtletas}
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    <div
                                        id="torneios"
                                        className={`tab-pane fade ${abaAtiva === 'torneios' ? 'show active' : ''}`}
                                        role="tabpanel"
                                        aria-labelledby="torneios-tab"
                                    >
                                        {renderEstadoNaoPopulado(torneiosState)}
                                        {torneiosState.connection && torneiosState.connection.totalItems > 0 && (
                                            <>
                                                <div className="row gy-3">
                                                    {torneiosState.connection.items.map((torneio) => (
                                                        <div key={torneio.id} className="col-md-6 col-lg-4">
                                                            <TorneioCardPesquisa torneio={torneio} />
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="mt-3">
                                                    <Pagination
                                                        currentPage={paginaTorneios}
                                                        totalPages={torneiosState.connection.totalPages}
                                                        onPageChange={setPaginaTorneios}
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    <div
                                        id="ligas"
                                        className={`tab-pane fade ${abaAtiva === 'ligas' ? 'show active' : ''}`}
                                        role="tabpanel"
                                        aria-labelledby="ligas-tab"
                                    >
                                        {renderEstadoNaoPopulado(ligasState)}
                                        {ligasState.connection && ligasState.connection.totalItems > 0 && (
                                            <>
                                                <div className="row gy-3">
                                                    {ligasState.connection.items.map((liga) => (
                                                        <div key={liga.id} className="col-md-6 col-lg-4">
                                                            <LigaCardPesquisa liga={liga} />
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="mt-3">
                                                    <Pagination
                                                        currentPage={paginaLigas}
                                                        totalPages={ligasState.connection.totalPages}
                                                        onPageChange={setPaginaLigas}
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    {abaSeguindoVisivel && (
                                        <div
                                            id="seguindo"
                                            className={`tab-pane fade ${abaAtiva === 'seguindo' ? 'show active' : ''}`}
                                            role="tabpanel"
                                            aria-labelledby="seguindo-tab"
                                        >
                                            {renderEstadoNaoPopulado(seguindoState)}
                                            {seguindoState.connection && seguindoState.connection.totalItems > 0 && (
                                                <>
                                                    <div className="row gy-3">
                                                        {seguindoState.connection.items.map((atleta) => (
                                                            <div key={atleta.id} className="col-md-6 col-lg-4">
                                                                <AtletaCard atleta={atleta} />
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div className="mt-3">
                                                        <Pagination
                                                            currentPage={paginaSeguindo}
                                                            totalPages={seguindoState.connection.totalPages}
                                                            onPageChange={setPaginaSeguindo}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                                </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default SearchPage;
